/**
 * Edit an agreement during sourcing — the drawer's edit button.
 *
 * Jeff, 2026-09-10: "an 'edit' button show up in the agreement drawer next to
 * the 'Agreed Terms' header… that when clicked would open up a modal with the
 * same 'new agreement' form so edits could be made during sourcing. Once an
 * agreement has been 'accepted', the edit should go away unless the agreement
 * moves back to 'negotiating'." (Both status names were renamed on 2026-09-14;
 * the rule is unchanged and `canEditAgreement` still derives it from the
 * current status.)
 *
 * ── The same form, not a second copy of it ──────────────────────────────────
 * The fields come from `AgreementFields`, which the create page renders too.
 * Two hand-rolled copies of twenty fields is exactly the drift item 69 found in
 * the drawers — where it had already cost the agreement panel its City and
 * State — so there is one list and both surfaces render it.
 *
 * ── Only what changed is sent ───────────────────────────────────────────────
 * PATCH applies whatever keys are present, and a blank string becomes null: so
 * sending every field would rewrite the whole row on every save, and the audit
 * diff would say a save changed nothing while listing twenty fields. `changed`
 * is the diff, and an empty diff does not write at all.
 *
 * ── Blanking a field is a real edit, and has to reach the server ────────────
 * The one thing a naive diff gets wrong: `''` is not "unchanged", it is
 * "clear this". So the diff compares against the value the agreement ARRIVED
 * with and sends `''` when a filled field was emptied — which the API's parser
 * turns into null. Dropping empties instead (the obvious `compact()` reflex,
 * and what the create form correctly does) would make a deletion silently
 * impossible.
 *
 * ── Status is not editable here ─────────────────────────────────────────────
 * See `AgreementFields`: status moves through the chip, which is the only path
 * that enforces the transition graph and asks for a reason on a closure.
 */
import { useMemo, useState } from 'react';
import { Modal } from '@/features/crm/components/Modal';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  AgreementFields,
  type AgreementFormValues,
} from '@/features/hotel-supplier/components/AgreementFields';
import { AmenityQuestionFields } from '@/features/hotel-supplier/components/AmenitiesModal';
import {
  fromPayload as amenitiesFromPayload,
  toPayload as amenitiesToPayload,
  type AmenityAnswers,
} from '@/features/hotel-supplier/amenities';

/** The slice of an agreement this modal edits. A structural type rather than
 *  the full `Agreement`, so the component is mountable from a test without
 *  inventing forty unrelated fields. */
export interface EditableAgreement {
  id: string;
  status: string;
  ownerTeam: string | null;
  tmc: string | null;
  negotiationId: string | null;
  termStart: string | null;
  termEnd: string | null;
  negotiatedCurrency: string | null;
  commissionRatePct: string | null;
  airportCodes: string | null;
  estimatedAnnualRoomNights: number | null;
  avgLengthOfStayRequested: string | null;
  lowestFixedRateRequested: string | null;
  dynamicDiscountPctRequested: string | null;
  hsmName: string | null;
  hsmEmail: string | null;
  hsmPhone: string | null;
  hotelPocName: string | null;
  hotelPocEmail: string | null;
  internalNote: string | null;
  hotelNote: string | null;
  amenities: Record<string, unknown> | null;
}

/**
 * Statuses at or past the acceptance handoff, plus the closures.
 *
 * `accepted` is absent on purpose and its absence is not an oversight: nothing
 * ever RESTS at accepted — the API stores `loading` for it — so an accepted
 * agreement is a `loading` one, and that is what closes the door Jeff asked to
 * close. Kept as its own exported predicate rather than reusing the drawer's
 * private `PAST_SOURCING`, because these two answer different questions that
 * happen to share a list today: one hides a display group, this one gates a
 * write.
 */
const PAST_EDITING = new Set([
  'loading',
  'active',
  'expired',
  'terminated',
  'declined',
  'dismissed',
]);

/**
 * Can this agreement still be edited?
 *
 * Derived from the CURRENT status and nothing else, which is what makes the
 * second half of Jeff's rule — "unless the agreement moves back to
 * negotiating" — true for free: there is no "was accepted" flag to clear, so a
 * move back into any sourcing status brings the button back on its own.
 *
 * Worth knowing: that move is not currently legal. `ALLOWED_TRANSITIONS` sends
 * `loading` only to `active` or `terminated`, so nothing can return to
 * negotiating today. This predicate is already correct for the day that
 * changes; adding the edge is a separate decision (feedback item 71).
 */
export function canEditAgreement(status: string): boolean {
  return !PAST_EDITING.has(status);
}

/** The agreement, as form values. Numbers and nulls become strings, because a
 *  form holds text and `value={null}` makes React warn and go uncontrolled. */
export function toFormValues(a: EditableAgreement): AgreementFormValues {
  const s = (v: string | number | null) => (v === null ? '' : String(v));
  return {
    owner_team: s(a.ownerTeam),
    tmc: s(a.tmc),
    negotiation_id: s(a.negotiationId),
    // Dates arrive as ISO instants or dates; `<input type="date">` needs
    // exactly YYYY-MM-DD and silently shows nothing for anything else.
    term_start: (a.termStart ?? '').slice(0, 10),
    term_end: (a.termEnd ?? '').slice(0, 10),
    negotiated_currency: s(a.negotiatedCurrency),
    commission_rate_pct: s(a.commissionRatePct),
    airport_codes: s(a.airportCodes),
    estimated_annual_room_nights: s(a.estimatedAnnualRoomNights),
    avg_length_of_stay_requested: s(a.avgLengthOfStayRequested),
    lowest_fixed_rate_requested: s(a.lowestFixedRateRequested),
    dynamic_discount_pct_requested: s(a.dynamicDiscountPctRequested),
    hsm_name: s(a.hsmName),
    hsm_email: s(a.hsmEmail),
    hsm_phone: s(a.hsmPhone),
    hotel_poc_name: s(a.hotelPocName),
    hotel_poc_email: s(a.hotelPocEmail),
    internal_note: s(a.internalNote),
    hotel_note: s(a.hotelNote),
    // Present so `AgreementFields` can read it, never rendered: `showStatus`
    // is false here and the chip owns transitions.
    status: s(a.status),
  };
}

/**
 * The keys whose value differs from what we loaded.
 *
 * Includes a field cleared to `''` — see the header. Excludes `status`, which
 * is in the form map only so the shared component can read it.
 */
export function changedFields(
  original: AgreementFormValues,
  current: AgreementFormValues,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(current)) {
    if (k === 'status') continue;
    if ((original[k] ?? '') !== v) out[k] = v;
  }
  return out;
}

export function AgreementEditModal({
  agreement,
  onClose,
  onSaved,
  testId = 'agreement-edit',
}: {
  agreement: EditableAgreement;
  onClose: () => void;
  /** The host refetches — the drawer shows the saved values, not our copy. */
  onSaved: () => void;
  testId?: string;
}) {
  const original = useMemo(() => toFormValues(agreement), [agreement]);
  const originalAmenities = useMemo(
    () => amenitiesFromPayload(agreement.amenities),
    [agreement.amenities],
  );

  const [form, setForm] = useState<AgreementFormValues>(original);
  const [amenities, setAmenities] = useState<AmenityAnswers>(originalAmenities);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const setAmenity = (k: string, v: string) =>
    setAmenities((a) => ({ ...a, [k]: v }));

  const fieldDiff = changedFields(original, form);
  const before = amenitiesToPayload(originalAmenities);
  const after = amenitiesToPayload(amenities);
  /*
   * Amenities are one jsonb value, so the unit of change is the whole blob.
   * Compared as canonical JSON rather than by reference: `useState` hands back
   * a new object on every keystroke, so an identity check would call every
   * save a change and rewrite the column on a modal somebody only opened.
   */
  const amenitiesChanged =
    JSON.stringify(before ?? null) !== JSON.stringify(after ?? null);
  const dirty = Object.keys(fieldDiff).length > 0 || amenitiesChanged;

  async function save() {
    // Guard the handler, not just the button: this form submits on Enter from
    // any of its twenty fields.
    if (saving || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/api/btp/agreements/${agreement.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...fieldDiff,
          /*
           * `null`, not omitted, when every answer was cleared. Omitting would
           * leave the old blob in place — so "I deleted these answers" and "I
           * did not touch them" would be the same request.
           */
          ...(amenitiesChanged ? { amenities: after ?? null } : {}),
        }),
      });
      onSaved();
    } catch (err) {
      // The API's own message. Its validator is more precise than anything
      // this form could say about a bad decimal or an impossible date.
      setError(
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : 'The changes could not be saved.',
      );
      setSaving(false);
    }
  }

  return (
    <Modal
      actions={
        <>
          <button
            className="btn btn-outline"
            data-testid={`${testId}-cancel`}
            disabled={saving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            data-testid={`${testId}-save`}
            disabled={saving || !dirty}
            onClick={() => void save()}
            type="button"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
      onClose={onClose}
      size="wide"
      testId={testId}
      title="Edit agreement"
    >
      <form
        data-testid={`${testId}-form`}
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p className="page-intro" style={{ marginTop: 0 }}>
          The customer and the hotel cannot be changed — an agreement for a
          different customer, or at a different property, is a different
          agreement. Status moves through the chip on the row, which is what
          keeps the workflow rules in force.
        </p>

        {error && (
          <div
            className="banner banner-error"
            data-testid={`${testId}-error`}
            role="alert"
          >
            {error}
          </div>
        )}

        <AgreementFields
          amenities={amenities}
          amenitiesSlot={
            <AmenityQuestionFields
              answers={amenities}
              set={setAmenity}
              testId={`${testId}-amenities`}
            />
          }
          form={form}
          onOpenAmenities={() => {}}
          set={set}
        />

        {!dirty && (
          <p
            className="page-intro"
            data-testid={`${testId}-clean`}
            style={{ marginBottom: 0 }}
          >
            Nothing has changed yet.
          </p>
        )}
      </form>
    </Modal>
  );
}
